/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: branding-distribution-overrides-selector-component.ts
 *
 * Created by randysorensen on 11/7/17.
 */

'use strict';

function BrandingDistributionOverridesSelectorController(this: JS5Object, _: JS5Object, $q: JS5Object,
		ModalService: JS5Object, ContentResourceFactory: JS5Object, NetworkResourceFactory: JS5Object,
		PackageResourceFactory: JS5Object, UtilsService: JS5Object) {


	const NoneAsset = {
		id: <number> null,
		name: <string> '(None)',
	};

	let overridden = (): boolean => {
		return this.partnerPackage[this.overrideAssetDescriptor.overrideFlagPropertyName];
	};

	//======//
	// Data //
	//======//

	let queryBranding = function (packageObject: JS5Object, containerType: string, destination: JS5Object): JS5Promise {
		return $q.all({
			contentBrandingContainers: ContentResourceFactory.getBranding({
				id: packageObject.content.id,
				view: containerType,
			}).$promise,
			networkBrandingContainers: NetworkResourceFactory.getBranding({
				id: packageObject.content.network.id,
				view: containerType,
			}).$promise,
		}).then((brandingData: JS5Object) => {
			let addAvailableContainer = (container: JS5Object) => {
				destination.push(container);
			};

			_.forEach(brandingData.networkBrandingContainers, addAvailableContainer);
			_.forEach(brandingData.contentBrandingContainers, addAvailableContainer);

			return destination;
		});
	};

	let getAvailableContainers = () => {
		this.availableContainers = [NoneAsset];
		switch (this.overrideAssetType) {
			case 'watermark':
				// watermarks still use old API
				PackageResourceFactory.query({
					id: this.partnerPackage.packageId,
					sub: 'watermarksByContent',
				}).$promise.then((availableWatermarkContainers: JS5Object[]) => {
					this.availableContainers = this.availableContainers.concat(availableWatermarkContainers);
				});
				break;

			// listing all of the cases, while shunting them to
			// default, just for readability/clarity
			case 'preRoll':
			case 'postRoll':
			case 'breakPrePostRoll':
			default:
				queryBranding(
					this.overrideContext.packageObject,
					this.overrideAssetType + 's',
					this.availableContainers);
				break;
		}
	};


	//================//
	// Template Logic //
	//================//

	this.dropDownEnabled = (): boolean => {
		return overridden() &&
			this.overrideContext.canWriteOverrides &&
			!this.overrideContext.readOnly;
	};

	this._viewProperty = (newProp: any) => {
		let overrideProp = this.overrideAssetDescriptor.overridePropertyName;
		let baseProp = this.overrideAssetDescriptor.basePropertyName;

		// set the container if a new one is provided (setter)
		if (newProp) {
			if (overridden()) {
				if (UtilsService.compareBasic(NoneAsset, newProp) === 0) {
					delete this.partnerPackage[overrideProp];
				} else {
					this.partnerPackage[overrideProp] = _.cloneDeep(newProp);
				}
			} else {
				console.warn(`selector modified but should be disabled`);
			}
		}


		// grab the correct container
		let container: JS5Object;
		if (overridden()) {
			container = this.partnerPackage[overrideProp];
		} else {
			// make sure there's no stale overridden property - it
			// should default to the original from the
			// subscription
			this.partnerPackage[overrideProp] = _.cloneDeep(this.partnerPackage.subscription[baseProp]);

			container = this.partnerPackage.subscription[baseProp];
		}

		// return the container (getter)
		if (!container) {
			return NoneAsset;
		} else {
			let containerName = container.name;
			return this.availableContainers.filter((availableContainer: JS5Object) => {
				return availableContainer.name === containerName;
			})[0];
		}
	};

	this._viewAssetSeasonVolumeFlag = (newFlag: boolean): boolean => {
		let flagPropName = this.overrideAssetDescriptor.seasonVolumeFlagPropertyName;

		if (newFlag !== undefined) {
			if (overridden()) {
				this.partnerPackage[flagPropName] = newFlag;
			} else {
				console.warn(`season/volume flag modified but should be disabled`);
			}
		}

		if (overridden()) {
			return this.partnerPackage[flagPropName];
		} else {
			// make sure there's no stale season/volume flag on
			// the partner package - it should default to the
			// original from the subscription
			this.partnerPackage[flagPropName] = this.partnerPackage.subscription[flagPropName];

			return this.partnerPackage.subscription[flagPropName];
		}
	};

	this.seasonVolumeCBEnabled = (): boolean => {
		return overridden() &&
			this.overrideContext.packageObject.content.type === 'Series' &&
			this._viewProperty() &&
			!this._viewProperty().networkId &&
			this.overrideContext.canWriteOverrides &&
			!this.overrideContext.readOnly;
	};

	this.previewEnabled = (): boolean => {
		return true;
	};

	let getSeasonVolumePreviewAsset = (type: string, assetPool: JS5Object[]): JS5Object => {
		let assetsFound = assetPool.filter((asset: JS5Object) => {
			return asset[type + 'Id'] === this.overrideContext.packageObject[type].id;
		});
		return assetsFound.length > 0 ? assetsFound[0] : null;
	};

	let getNonSeasonVolumePreviewAsset = (assetPool: JS5Object[]): JS5Object => {
		let assetsFound = assetPool.filter((asset: JS5Object) => {
			return !asset.volumeId && !asset.seasonId;
		});
		return assetsFound.length > 0 ? assetsFound[0] : null;
	};


	this.getPreviewInfo = (previewInfo: JS5Object): JS5Object => {
		let assetPool = this._viewProperty()[this.overrideAssetDescriptor.containerAssetsPropertyName];
		let sv = this._viewAssetSeasonVolumeFlag();
		let assetObject: JS5Object;

		if (sv) {
			if (this.partnerPackage.subscription.seasonSubscription) {
				assetObject = getSeasonVolumePreviewAsset('season', assetPool);
			} else {
				assetObject = getSeasonVolumePreviewAsset('volume', assetPool);
			}
		}

		if (!assetObject) {
			assetObject = getNonSeasonVolumePreviewAsset(assetPool);
		}

		if (previewInfo.subPropertyName) {
			return assetObject[previewInfo.subPropertyName];
		} else {
			return assetObject;
		}
	};

	this.openMediaModal = ($event: JS5Object, assetInfo: JS5Object) => {
		ModalService.showMediaModal(assetInfo.watermarkId ? 'watermark' : 'video', assetInfo);
		$event.currentTarget.blur();
	};

	this.getDefaultLabel = () => {
		let defaultContainer = this.partnerPackage.subscription[this.overrideAssetDescriptor.basePropertyName];
		let defaultLabel = {
			name: '',
			svLabel: '',
		};
		if (defaultContainer) {
			let defaultContainerSV = this.partnerPackage.subscription[this.overrideAssetDescriptor.seasonVolumeFlagPropertyName];
			defaultLabel.name = defaultContainer.name;
			if (defaultContainerSV) {
				defaultLabel.svLabel = this.partnerPackage.subscription.seasonSubscription ? '(Season)' : '(Volume)';
			}
		} else {
			defaultLabel.name = NoneAsset.name;
		}

		return defaultLabel;
	};


	//=================//
	// Lifecycle Hooks //
	//=================//

	this.$onInit = () => {
		if (!this.partnerPackage[this.overrideAssetDescriptor.basePropertyName]) {
			this.partnerPackage[this.overrideAssetDescriptor.basePropertyName] = _.cloneDeep(NoneAsset);
		}
		getAvailableContainers();
	};
}

angular
.module('prism-ui.package')
.component('brandingDistributionOverridesSelector', {
	controller: BrandingDistributionOverridesSelectorController,
	bindings: {
		overrideContext: '=',
		overrideAssetDescriptor: '=',
		overrideAssetType: '=',
		partnerPackage: '=',
	},
	templateUrl: 'package/editor/components/modals/brandingDistributionOverrides/branding-distribution-overrides-selector-template.html',
});
